/**
 * Client-side mirror of ps-backstage-infra/scripts/ttl_calendar.py.
 * Weekend rules use one team-wide timezone (not the browser).
 */

export const TEAM_TTL_TIMEZONE = 'America/New_York';

export const MAX_ADD_DAYS = 3;
export const MAX_ADD_HOURS_TOTAL = MAX_ADD_DAYS * 24;
export const MAX_LIFETIME_DAYS = 14;
export const WEEKEND_SNAP_HOUR = 9;

/** Parts of an absolute instant in the team timezone. */
function zonedParts(date: Date, timeZone: string = TEAM_TTL_TIMEZONE) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  }
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return {
    weekday: weekdayMap[map.weekday] ?? 0,
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/** Approximate UTC Date for local Y-M-D HH:00 in timeZone (preview only). */
function zonedLocalToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  timeZone: string = TEAM_TTL_TIMEZONE,
): Date {
  let utc = Date.UTC(year, month - 1, day, hour, 0, 0);
  for (let i = 0; i < 3; i += 1) {
    const parts = zonedParts(new Date(utc), timeZone);
    const asLocal = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    const want = Date.UTC(year, month - 1, day, hour, 0, 0);
    utc += want - asLocal;
  }
  return new Date(utc);
}

export function snapWeekendExpiration(date: Date): Date {
  const parts = zonedParts(date);
  if (parts.weekday !== 0 && parts.weekday !== 6) {
    return date;
  }
  const daysAhead = parts.weekday === 6 ? 2 : 1;
  const base = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day + daysAhead),
  );
  return zonedLocalToUtc(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate(),
    WEEKEND_SNAP_HOUR,
  );
}

export function clampMaxLifetime(
  expiration: Date,
  createdAt: Date | undefined,
  maxDays: number = MAX_LIFETIME_DAYS,
): Date {
  if (!createdAt || Number.isNaN(createdAt.getTime())) {
    return expiration;
  }
  const cap = new Date(createdAt.getTime() + maxDays * 24 * 60 * 60 * 1000);
  return expiration.getTime() <= cap.getTime() ? expiration : cap;
}

export function previewExtendedExpiration(opts: {
  currentExpiration: Date;
  createdAt?: Date;
  addDays: number;
  addHours: number;
}): Date {
  const added = (opts.addDays * 24 + opts.addHours) * 60 * 60 * 1000;
  let next = new Date(opts.currentExpiration.getTime() + added);
  next = snapWeekendExpiration(next);
  next = clampMaxLifetime(next, opts.createdAt);
  // Never preview a date earlier than the current expiration (long initial TTLs).
  if (next.getTime() < opts.currentExpiration.getTime()) {
    return opts.currentExpiration;
  }
  return next;
}

/** Friday (team TZ) + expiry before next Monday 09:00, or expiry on Sat/Sun. */
export function shouldWarnWeekendExpiry(
  expiration: Date,
  now: Date = new Date(),
): boolean {
  if (expiration.getTime() <= now.getTime()) {
    return false;
  }
  const expParts = zonedParts(expiration);
  if (expParts.weekday === 0 || expParts.weekday === 6) {
    return true;
  }
  const nowParts = zonedParts(now);
  if (nowParts.weekday !== 5) {
    return false;
  }
  const monday = zonedLocalToUtc(
    nowParts.year,
    nowParts.month,
    nowParts.day + 3,
    WEEKEND_SNAP_HOUR,
  );
  return expiration.getTime() <= monday.getTime();
}
